import { useState } from 'react';
import { Pencil, Trash2, X, Check } from 'lucide-react';
import { Modal } from '../ui/Modal/Modal';
import { ConfirmModal } from '../ui/ConfirmModal/ConfirmModal';
import { Button } from '../ui/Button/Button';
import { Spinner } from '../ui/Spinner/Spinner';
import { extractErrorMessage } from '../../utils/errorMessage';
import {
  useCreateNote,
  useDeleteNote,
  useEntityNotes,
  useUpdateNote,
} from './useEntityNotes';
import type { NoteDto } from './useEntityNotes';
import styles from './NotesPanel.module.css';

interface NotesPanelProps {
  open: boolean;
  onClose: () => void;
  entityType: 'receipt' | 'wb_ticket';
  encId: string;
  entityLabel: string;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface NoteItemProps {
  note: NoteDto;
  onEdit: (id: number, body: string) => Promise<void>;
  onDelete: (id: number) => void;
}

function NoteItem({ note, onEdit, onDelete }: NoteItemProps) {
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(note.body);
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleEditSave() {
    const trimmed = editBody.trim();
    if (!trimmed) return;
    setSaving(true);
    setEditError(null);
    try {
      await onEdit(note.id, trimmed);
      setEditing(false);
    } catch (err) {
      // Keep edit mode open on failure — closing here would silently
      // discard the edit and leave the stale note displayed.
      setEditError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function handleEditCancel() {
    setEditBody(note.body);
    setEditError(null);
    setEditing(false);
  }

  return (
    <div className={styles.note}>
      <div className={styles.noteMeta}>
        <span className={styles.noteAuthor}>{note.created_by_name}</span>
        <span>{formatTimestamp(note.created_at)}</span>
        {note.updated_at !== note.created_at && (
          <span>(edited {formatTimestamp(note.updated_at)})</span>
        )}
        {(note.can_edit || note.can_delete) && (
          <div className={styles.noteActions}>
            {note.can_edit && !editing && (
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setEditing(true)}
                aria-label="Edit note"
                title="Edit"
              >
                <Pencil size={13} aria-hidden />
              </button>
            )}
            {note.can_delete && !editing && (
              <button
                type="button"
                className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                onClick={() => onDelete(note.id)}
                aria-label="Delete note"
                title="Delete"
              >
                <Trash2 size={13} aria-hidden />
              </button>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <>
          <textarea
            className={styles.editArea}
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            aria-label="Edit note text"
            autoFocus
          />
          {editError !== null && <p className={styles.error}>{editError}</p>}
          <div className={styles.editActions}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={handleEditCancel}
              disabled={saving}
              aria-label="Cancel edit"
              title="Cancel"
            >
              <X size={13} aria-hidden />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={handleEditSave}
              disabled={!editBody.trim() || saving}
              aria-label="Save edit"
              title="Save"
            >
              <Check size={13} aria-hidden />
            </button>
          </div>
        </>
      ) : (
        <p className={styles.noteBody}>{note.body}</p>
      )}
    </div>
  );
}

export function NotesPanel({ open, onClose, entityType, encId, entityLabel }: NotesPanelProps) {
  const [newBody, setNewBody] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, isLoading } = useEntityNotes(entityType, open ? encId : undefined);
  const createMutation = useCreateNote(entityType, encId);
  const updateMutation = useUpdateNote(entityType, encId);
  const deleteMutation = useDeleteNote(entityType, encId);

  const notes = data?.notes ?? [];

  async function handleSubmit() {
    const body = newBody.trim();
    if (!body) return;
    setCreateError(null);
    try {
      await createMutation.mutateAsync(body);
      setNewBody('');
    } catch (err) {
      // Leave the typed text in place and show why it didn't save — a
      // failed submit must not look identical to a no-op click.
      setCreateError(extractErrorMessage(err));
    }
  }

  // Rejection bubbles to the caller (NoteItem), which keeps edit mode
  // open and renders its own inline error — this function must not
  // swallow it here.
  function handleEdit(noteId: number, body: string): Promise<void> {
    return updateMutation.mutateAsync({ noteId, body }).then(() => undefined);
  }

  function setDeleteTarget(id: number | null): void {
    setDeleteError(null);
    setDeleteTargetId(id);
  }

  async function handleDeleteConfirm() {
    if (deleteTargetId === null) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch (err) {
      // Keep the confirm modal open with a reason instead of silently
      // stopping the loading state, which looked identical to success.
      setDeleteError(extractErrorMessage(err));
    }
  }

  return (
    <>
      <Modal open={open} onClose={onClose} title={`Staff Notes — ${entityLabel}`} size="md">
        {isLoading ? (
          <div className={styles.empty}>
            <Spinner size="sm" />
          </div>
        ) : notes.length === 0 ? (
          <p className={styles.empty}>No notes yet.</p>
        ) : (
          <div className={styles.notesList}>
            {notes.map((note) => (
              <NoteItem
                key={note.id}
                note={note}
                onEdit={handleEdit}
                onDelete={(id) => setDeleteTarget(id)}
              />
            ))}
          </div>
        )}

        <div className={styles.addForm}>
          <label className={styles.addLabel} htmlFor="new-note-body">
            Add note
          </label>
          <div className={styles.addRow}>
            <textarea
              id="new-note-body"
              className={styles.addArea}
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              placeholder="Write a note…"
            />
          </div>
          {createError !== null && <p className={styles.error}>{createError}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!newBody.trim() || createMutation.isPending}
              loading={createMutation.isPending}
            >
              Submit
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteTargetId !== null}
        title="Delete note"
        message={
          <>
            <p>Are you sure you want to delete this note? This cannot be undone.</p>
            {deleteError !== null && <p className={styles.error}>{deleteError}</p>}
          </>
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />
    </>
  );
}
